import Cookies from 'universal-cookie';

/**
 * UTM / ad attribution capture and persistence.
 *
 * WHY A COOKIE. Attribution used to be read live off `window.location.search`
 * at submit time and nowhere else, so it survived exactly as long as the
 * visitor stayed on the landing URL. One internal navigation — or the
 * country/language redirect — and every campaign parameter was gone, while
 * `?a=` and `?ref=` had been getting 15-day cookies for years. This puts UTM on
 * the same footing.
 *
 * The cookie is written on the SHARED PARENT DOMAIN, so attribution captured
 * here also reaches app.shipper.market / app.shipper.partners, whose own
 * registration forms read it. That is the only way a campaign link pointed
 * straight at an app can be attributed at all.
 *
 * LAST-TOUCH at capture, URL-WINS at read: a fresh click always overwrites the
 * stored value, and a URL that carries parameters beats whatever is stored.
 * (The backend is separately FIRST-touch — it never overwrites an
 * organization's existing attribution.)
 */

export const UTM_COOKIE_NAME = 'utm_attribution';

const COOKIE_TTL_DAYS = 15; // matches the affiliation / referral_code cookies

/** Keys read from the query string. */
export const UTM_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'campaign_id',
  'ad_id',
  'adset_id',
  'fbclid',
  'gclid'
];

/** Everything that may end up in the payload, including derived values. */
export const UTM_PAYLOAD_KEYS = [...UTM_QUERY_KEYS, 'referrer_url', 'landing_page'];

const isBrowser = () => typeof window !== 'undefined';

const cookieOptions = () => {
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_TTL_DAYS);

  return {
    path: '/',
    domain: process.env.GATSBY_APP_COOKIE_DOMAIN,
    expires,
    secure: process.env.GATSBY_APP_COOKIE_SECURE
  };
};

/** Attribution present in the current URL, or `{}`. */
export const utmParamsFromUrl = () => {
  if (!isBrowser()) return {};

  const params = new URLSearchParams(window.location.search);
  const utm = {};

  UTM_QUERY_KEYS.forEach(key => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });

  // Only meaningful alongside a real parameter — on its own, `landing_page` is
  // just the path the visitor happened to open, and the backend deliberately
  // ignores an attribution payload that carries nothing else.
  if (Object.keys(utm).length > 0) {
    if (document.referrer) utm.referrer_url = document.referrer;
    utm.landing_page = window.location.pathname;
  }

  return utm;
};

/** Previously stored attribution, or `{}`. */
export const utmParamsFromCookie = () => {
  if (!isBrowser()) return {};

  try {
    const stored = new Cookies().get(UTM_COOKIE_NAME);
    if (!stored || typeof stored !== 'object') return {};

    // Never trust the shape — a cookie is user-writable.
    return UTM_PAYLOAD_KEYS.reduce((acc, key) => {
      if (typeof stored[key] === 'string' && stored[key] !== '') acc[key] = stored[key];
      return acc;
    }, {});
  } catch (e) {
    return {};
  }
};

/**
 * Persist whatever the current URL carries. No-op when the URL carries nothing,
 * so an internal navigation never clears an earlier capture.
 */
export const persistUtmParams = () => {
  if (!isBrowser()) return;

  const fromUrl = utmParamsFromUrl();
  if (Object.keys(fromUrl).length === 0) return;

  try {
    new Cookies().set(UTM_COOKIE_NAME, fromUrl, cookieOptions());
  } catch (e) {
    // Attribution must never break a page load.
  }
};

/**
 * The payload to send with a registration or lead mutation. URL wins so an
 * explicit click is never masked by a stale cookie.
 */
export const readUtmParams = () => ({
  ...utmParamsFromCookie(),
  ...utmParamsFromUrl()
});
